import type { KameleoonService } from '@core/services/kameleoon/kameleoon.service';
import type { SDKConfigurationType } from '@kameleoon/javascript-sdk';
import type { TrackerConfigInterface } from '@wizbii/utils/tracking';

import { signal } from '@angular/core';
import Bugsnag from '@bugsnag/js';
import { environment } from '@environment';
import { CustomData, Environment, KameleoonClient } from '@kameleoon/javascript-sdk';
import { createKameleoonConfig, KAMELEOON_LEGAL_CONSENT_EVENT } from '@wizbii/utils/tracking';

export function configKameleoon(
  siteCode: string,
  userId?: string,
  kameleoonService?: KameleoonService
): TrackerConfigInterface {
  const lang = environment.i18n.lang;
  const locale = environment.i18n.locale;
  const configuration: Partial<SDKConfigurationType> = {
    environment:
      environment.platform === 'prod'
        ? Environment.Production
        : environment.platform === 'staging'
          ? Environment.Staging
          : Environment.Development,
    cookieDomain: environment.i18n.domainsByLocale[locale] as string,
  };

  const client = new KameleoonClient({ siteCode, configuration });

  // Mirror the script-side consent onto the SDK client. The consent source of truth is the
  // WizbiiGdpr widget: on change it toggles the script tracker's load/unload, which dispatches
  // KAMELEOON_LEGAL_CONSENT_EVENT. We default to `false` (no consent) until told otherwise, and
  // replay the last known value once the async `initialize()` has resolved.
  const latestConsent = signal(false);
  const clientReady = signal(false);
  const applyConsent = (): void => {
    if (clientReady()) {
      client.setLegalConsent(client.getVisitorCode(), latestConsent());
    }
  };

  if (typeof document !== 'undefined') {
    document.addEventListener(KAMELEOON_LEGAL_CONSENT_EVENT, (event) => {
      latestConsent.set(!!(event as CustomEvent<{ consent?: boolean }>).detail.consent);
      applyConsent();
    });
  }

  client
    .initialize()
    .then(() => {
      clientReady.set(true);
      const visitorCode = client.getVisitorCode();

      client.addData(visitorCode, new CustomData('lang', lang));
      client.addData(visitorCode, new CustomData('version', environment.version));
      client.addData(visitorCode, new CustomData('applicationId', environment.applicationId));

      if (userId) {
        client.addData(visitorCode, new CustomData('userId', userId));
      }

      applyConsent();

      // Expose the initialized client so app services (e.g. contact modal debug info) can read
      // the visitor's active AB test variations without owning the SDK lifecycle.
      kameleoonService?.registerClient(client);
    })
    .catch((err: unknown) => {
      Bugsnag.notify('Unable to initialize Kameleoon SDK', (event) => {
        event.severity = 'warning';
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        event.addMetadata('cause', err as any);
      });
    });

  return createKameleoonConfig({ siteCode });
}
